import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Box,
  Paper,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  LinearProgress,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../theme";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";

import {
  EventNote,
  Contacts,
  LocationOn,
  TrendingUp,
  TrendingDown,
  RefreshRounded,
  MoreVertRounded,
  CalendarTodayRounded,
  StarRounded,
} from "@mui/icons-material";

import { DashboardData } from "../../types/dashboard";
import ForecastTable from "./ForecastTable";
import VenueWidget from "./VenueWidget";
import { OperationsIcon } from "../../../../layout/sidebar/icons";
import apiService from "../../../../service/Api";
import PaymentStatusWidget from "./PaymentStatusWidget";

interface AdminDashboardProps {
  data: DashboardData;
}

interface BookingStats {
  cancelled: number;
  confirmed: number;
  enquiry: number;
  fulfilled: number;
  lost: number;
  testing: number;
  total: number;
}

interface BookingStatusChartItem {
  id: number;
  value: number;
  label: string;
  color: string;
}

// Enhanced Animated Metric Card
export const EnhancedMetricCard: React.FC<{
  title: string;
  value: number;
  icon: React.ComponentType<Record<string, unknown>>;
  color: string;
  trend?: number;
  subtitle?: string;
}> = ({ title, value, icon: Icon, color, trend, subtitle }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1200;
    const increment = value / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 2,
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: "1px solid",
        borderColor: `${color}30`,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 16px ${color}25`,
          borderColor: color,
          "& .metric-icon": {
            transform: "rotate(10deg) scale(1.1)",
          },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: -30,
          right: -30,
          width: "100px",
          height: "100px",
          background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
          borderRadius: "50%",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box
          className="metric-icon"
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 8px ${color}40`,
            transition: "transform 0.4s ease",
          }}
        >
          <Icon sx={{ color: "white", fontSize: 24 }} />
        </Box>
        {trend !== undefined && (
          <Chip
            icon={trend >= 0 ? <TrendingUp /> : <TrendingDown />}
            label={`${trend >= 0 ? "+" : ""}${trend}%`}
            size="small"
            sx={{
              backgroundColor: trend >= 0 ? "#10b98125" : "#ef444425",
              color: trend >= 0 ? "#10b981" : "#ef4444",
              fontWeight: 700,
              fontSize: "0.65rem",
              "& .MuiChip-icon": {
                color: "inherit",
              },
            }}
          />
        )}
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          mb: 0.5,
          color: color,
          fontSize: "2rem",
          lineHeight: 1,
          letterSpacing: "-0.02em",
        }}
      >
        {displayValue.toLocaleString()}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontWeight: 600,
          mb: subtitle ? 0.5 : 0,
          fontSize: "0.75rem",
        }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          variant="caption"
          sx={{
            color: "text.disabled",
            fontWeight: 500,
            fontSize: "0.65rem",
          }}
        >
          {subtitle}
        </Typography>
      )}

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${color} 0%, ${color}60 100%)`,
          opacity: 0.6,
        }}
      />
    </Paper>
  );
};

// Enhanced Chart Container
const EnhancedChartWidget: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  height?: string | number;
}> = ({ title, subtitle, children, actions, height = "100%" }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height,
        p: 2,
        borderRadius: 2,
        backgroundColor: colors.primary[900],
        border: "1px solid",
        borderColor: "grey.200",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
          borderColor: "primary.main",
          "& .chart-actions": {
            opacity: 1,
          },
        },
      }}
    >
      {refreshing && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            zIndex: 10,
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "text.primary",
              mb: subtitle ? 0.5 : 0,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                fontSize: "0.7rem",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box
          className="chart-actions"
          sx={{
            display: "flex",
            gap: 0.5,
            opacity: 0,
            transition: "opacity 0.3s",
          }}
        >
          {actions}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="More">
            <IconButton size="small">
              <MoreVertRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {children}
    </Paper>
  );
};

// Stats Badge Component
const StatBadge: React.FC<{
  icon: React.ComponentType<Record<string, unknown>>;
  label: string;
  value: string | number;
  color: string;
}> = ({ icon: Icon, label, value, color }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      px: 1.5,
      py: 0.5,
      borderRadius: 1.5,
      backgroundColor: `${color}12`,
      border: "1px solid",
      borderColor: `${color}30`,
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 4px 12px ${color}25`,
        borderColor: color,
      },
    }}
  >
    <Icon sx={{ fontSize: 16, color }} />
    <Box>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: "block",
          lineHeight: 1,
          fontSize: "0.65rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color, lineHeight: 1.3, fontSize: "0.8rem" }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const AdminDashboard: React.FC<AdminDashboardProps> = ({ data }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  // Calculate trends (mock data - replace with actual calculations)
  const bookingTrend = 12;
  const streamTrend = 5;
  const contactTrend = -3;
  const venueTrend = 15;

  // Calculate additional metrics
  const totalBookings = data.total_bookings;
  const avgBookingsPerDay =
    data.booking_trends.data.length > 0
      ? (
          data.booking_trends.data.reduce((a, b) => a + b, 0) /
          data.booking_trends.data.length
        ).toFixed(0)
      : 0;

  const peakBookingDay =
    data.booking_trends.data.length > 0
      ? data.booking_trends.labels[
          data.booking_trends.data.indexOf(
            Math.max(...data.booking_trends.data),
          )
        ]
      : "N/A";

  // Calculate Y-axis range with leeway for better visualization
  const allTrendData = [
    ...data.booking_trends.data,
    ...(data.booking_trends.stream_data || []),
  ];
  const trendMax = allTrendData.length > 0 ? Math.max(...allTrendData) : 0;
  const trendMin = allTrendData.length > 0 ? Math.min(...allTrendData) : 0;
  const yAxisPadding = Math.max(Math.ceil((trendMax - trendMin) * 0.25), 5);
  const yAxisMax = trendMax + yAxisPadding;
  const yAxisMin = Math.max(trendMin - yAxisPadding, 0);

  const primaryColor = "#4285f4";
  const successColor = "#10b981";
  const purpleColor = "#8b5cf6";

  const [stats, setStats] = useState<BookingStats | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.get<BookingStats>(
        `/api/bookings/booking/statistics/`,
      );
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch booking statistics:", error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const bookingStatusData: BookingStatusChartItem[] = stats
    ? [
        {
          id: 0,
          label: "Enquiry",
          value: stats.enquiry,
          color: "#F79E1B",
        },
        {
          id: 1,
          label: "Confirmed",
          value: stats.confirmed,
          color: "#4BA663",
        },
        {
          id: 2,
          label: "Fulfilled",
          value: stats.fulfilled,
          color: "#0000FF",
        },
        {
          id: 3,
          label: "Cancelled",
          value: stats.cancelled,
          color: "#EB4335",
        },
        {
          id: 4,
          label: "Testing",
          value: stats.testing,
          color: "#4E342E",
        },
        {
          id: 5,
          label: "Lost",
          value: stats.lost,
          color: "#616161",
        },
      ].filter((item) => item.value > 0)
    : [];

  return (
    <Box
      sx={{
        flexGrow: 1,
        background: `linear-gradient(180deg, ${colors.primary[800]} 0%, ${colors.primary[800]} 100%)`,
        minHeight: "100vh",
        pb: 4,
      }}
    >
      <Grid container spacing={2}>
        {/* Enhanced Metric Cards */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <EnhancedMetricCard
            title="Total Bookings"
            value={data.total_bookings}
            icon={EventNote}
            color="#614338"
            trend={bookingTrend}
            subtitle="Active events"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <EnhancedMetricCard
            title="Total Streams"
            value={data.total_streams}
            icon={OperationsIcon}
            color="#4285f4"
            trend={streamTrend}
            subtitle="All streams"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <EnhancedMetricCard
            title="Total Contacts"
            value={data.total_contacts}
            icon={Contacts}
            color="#10b981"
            trend={contactTrend}
            subtitle="In database"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <EnhancedMetricCard
            title="Total Venues"
            value={data.total_venues}
            icon={LocationOn}
            color="#f59e0b"
            trend={venueTrend}
            subtitle="Available locations"
          />
        </Grid>

        {/* Enhanced Booking Trends Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <EnhancedChartWidget
            title="Booking Trends"
            subtitle="Overview of booking activity over time"
            height="auto"
          >
            <Box sx={{ mb: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <StatBadge
                icon={CalendarTodayRounded}
                label="Peak Day"
                value={peakBookingDay}
                color={primaryColor}
              />
              <StatBadge
                icon={TrendingUp}
                label="Avg/Day"
                value={avgBookingsPerDay}
                color={successColor}
              />
              <StatBadge
                icon={StarRounded}
                label="Total"
                value={totalBookings}
                color={purpleColor}
              />
            </Box>

            <LineChart
              xAxis={[
                {
                  scaleType: "point",
                  data: data.booking_trends.labels,
                  tickLabelStyle: {
                    fontSize: 12,
                    fill: "#666",
                  },
                },
              ]}
              yAxis={[
                {
                  min: yAxisMin,
                  max: yAxisMax,
                  tickNumber: 6,
                  tickLabelStyle: {
                    fontSize: 11,
                    fill: "#666",
                  },
                  label: "Bookings",
                  labelStyle: {
                    fontSize: 12,
                    fill: "#444",
                  },
                },
              ]}
              series={[
                {
                  data: data.booking_trends.data,
                  label: "Bookings",
                  color: primaryColor,
                  curve: "catmullRom",
                  showMark: true,
                  area: true,
                },
                {
                  data: data.booking_trends.stream_data || [],
                  label: "Streams",
                  color: successColor,
                  curve: "catmullRom",
                  showMark: true,
                  area: true,
                },
              ]}
              height={420}
              margin={{ top: 20, bottom: 80, left: 80, right: 20 }}
              grid={{ vertical: false, horizontal: true }}
              sx={{
                "& .MuiLineElement-root": {
                  strokeWidth: 3,
                },
                "& .MuiMarkElement-root": {
                  r: 4,
                  stroke: "#fff",
                  strokeWidth: 2,
                },
                "& .MuiAreaElement-root": {
                  fillOpacity: 0.15,
                },
                "& .MuiChartsGrid-line": {
                  stroke: "rgba(0, 0, 0, 0.08)",
                  strokeDasharray: "3 3",
                },
              }}
            />
          </EnhancedChartWidget>
        </Grid>

        {/* Enhanced Booking Status Pie Chart */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <EnhancedChartWidget
            title="Booking Status"
            subtitle="Distribution of booking states"
            height="500px"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <PieChart
                series={[
                  {
                    data: bookingStatusData,
                    innerRadius: 50,
                    outerRadius: 90,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    highlightScope: { fade: "global", highlight: "item" },
                    faded: {
                      innerRadius: 20,
                      additionalRadius: -20,
                      color: "gray",
                    },
                  },
                ]}
                height={200}
                margin={{ top: 15, bottom: 15, left: 15, right: 15 }}
              />

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                {bookingStatusData.map((status, idx) => (
                  <Chip
                    key={idx}
                    avatar={
                      <Avatar
                        sx={{
                          bgcolor: status.color || primaryColor,
                          width: 16,
                          height: 16,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            bgcolor: "background.paper",
                            borderRadius: "50%",
                          }}
                        />
                      </Avatar>
                    }
                    label={`${status.label}: ${status.value}`}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: `${status.color}15`,
                      border: "1px solid",
                      borderColor: `${status.color}30`,
                      fontSize: "0.7rem",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </EnhancedChartWidget>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <EnhancedChartWidget
            title="Payment Status"
            subtitle="Distribution of payment states"
            height="500px"
          >
            <PaymentStatusWidget />
          </EnhancedChartWidget>
        </Grid>
        {/* Forecast Table */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <EnhancedChartWidget
            title="10-Day Forecast"
            subtitle="Upcoming booking predictions"
          >
            <ForecastTable data={data.ten_day_forecast} />
          </EnhancedChartWidget>
        </Grid>

        {/* Additional Stats Panel */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              height: "100%",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -80,
                right: -80,
                width: "200px",
                height: "200px",
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
                borderRadius: "50%",
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, mb: 2, fontSize: "1rem" }}
            >
              Quick Stats
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.9, mb: 1, fontSize: "0.75rem" }}
                >
                  Total Activity
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, fontSize: "2rem" }}
                >
                  {(data.total_bookings + data.total_contacts).toLocaleString()}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={75}
                  sx={{
                    mt: 1.5,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 3,
                      backgroundColor: "background.paper",
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.9, mb: 1, fontSize: "0.75rem" }}
                >
                  Conversion Rate
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, fontSize: "2rem" }}
                >
                  {data.total_contacts > 0
                    ? (
                        (data.total_bookings / data.total_contacts) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={
                    data.total_contacts > 0
                      ? (data.total_bookings / data.total_contacts) * 100
                      : 0
                  }
                  sx={{
                    mt: 1.5,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 3,
                      backgroundColor: "background.paper",
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.9, mb: 1, fontSize: "0.75rem" }}
                >
                  Venues Per User
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, fontSize: "2rem" }}
                >
                  {data.total_users > 0
                    ? (data.total_venues / data.total_users).toFixed(1)
                    : 0}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={60}
                  sx={{
                    mt: 1.5,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 3,
                      backgroundColor: "background.paper",
                    },
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Venue Widget */}
        <Grid size={{ xs: 12 }}>
          <EnhancedChartWidget
            title="Venue Analytics"
            subtitle="Comprehensive venue performance metrics"
          >
            <VenueWidget data={data} />
          </EnhancedChartWidget>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
